"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { useAssessmentAnalysis } from "@/hooks/use-assessment-analysis";
import { useMockAssessmentAnalysis } from "@/hooks/use-mock-assessment-analysis";
import { motion, AnimatePresence } from "framer-motion";
import { EmotionalMirrorContent } from "./EmotionalMirror";
import {
  Check,
  Clock,
  TrendingUp,
  Users,
  Target,
  CheckCircle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import posthog from "posthog-js";
import { ContinueButton } from "@/components/assessment/ContinueButton";
import { metaPixelEvents, generateEventId } from '@/lib/metaPixel';
import { buildMinitoolsSpecialOfferPath } from "@timeblock/assessment-content/utils/minitools-url";

export interface AssessmentResultsProps {
  runId: string;
  publicAccessToken: string;
  email?: string;
  assessmentTitle?: string;
  assessmentType?: string;
  isTestMode?: boolean;
  testResultsType?: "generic" | "ai";
}

function LoadingState({ assessmentType }: { assessmentType?: string }) {
  useEffect(() => {
    posthog.capture('minitools_results_loading_started', {
      assessment_type: assessmentType,
      minitools_assessment_type: assessmentType,
    });
  }, [assessmentType]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section Skeleton */}
      <div className="text-center py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="w-16 h-16 mx-auto mb-6 bg-gray-200 rounded-full animate-pulse" />
          <div className="h-8 w-96 mx-auto bg-gray-200 rounded animate-pulse mb-4" />
          <div className="h-6 w-64 mx-auto bg-gray-200 rounded animate-pulse" />
        </div>
      </div>

      {/* Results Section Skeleton */}
      <div className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2
            className="text-2xl font-bold text-center mb-8"
            style={{ color: "#2A6354" }}
          >
            Your Assessment Results
          </h2>

          {/* Score/Category Skeleton */}
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="text-center">
              <div className="h-12 w-32 mx-auto bg-gray-200 rounded animate-pulse mb-2" />
              <div className="h-4 w-40 mx-auto bg-gray-200 rounded animate-pulse" />
            </div>
            <div className="text-center">
              <div className="h-8 w-48 mx-auto bg-gray-200 rounded animate-pulse mb-2" />
              <div className="h-4 w-32 mx-auto bg-gray-200 rounded animate-pulse" />
            </div>
          </div>

          {/* Insights Skeleton */}
          <div className="mt-8">
            <h3 className="text-xl font-semibold mb-4">
              Key Insights About You
            </h3>
            <div className="grid gap-4">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="flex items-start p-4 bg-gray-50 rounded-lg"
                >
                  <div className="w-5 h-5 mr-3 bg-gray-300 rounded animate-pulse" />
                  <div className="flex-1 h-4 bg-gray-200 rounded animate-pulse" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Continue button during loading */}
      <div className="py-8">
        <ContinueButton
          onClick={() => {
            // Button is disabled during loading
          }}
          disabled={true}
          loading={true}
          loadingText="Analyzing your responses..."
          text="Continue"
        />
      </div>
    </div>
  );
}

function CompletionHero({
  category,
  assessmentTitle,
  email,
}: {
  category?: string;
  assessmentTitle?: string;
  email?: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="bg-gray-50"
    >
      <div className="max-w-4xl mx-auto px-4 py-12">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="w-16 h-16 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center"
        >
          <CheckCircle className="w-8 h-8 text-green-600" />
        </motion.div>

        <h1
          className="text-3xl md:text-4xl font-bold mb-4 text-center"
          style={{ color: "#2A6354" }}
        >
          Your Personalized Report is Being Prepared
        </h1>

        <p className="text-lg text-gray-600 mb-4 text-center">
          {category
            ? `You've been identified as: ${category}`
            : "Thank you for taking the time to share your unique situation."}
        </p>

        {/* Email Delivery Section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="border-t border-gray-200 pt-8 mt-8"
        >
          <div className="bg-gray-50 rounded-lg p-6 max-w-2xl mx-auto">
            <div className="mb-4">
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <motion.div
                  className="bg-green-600 h-2 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: "100%" }}
                  transition={{ delay: 0.5, duration: 2 }}
                />
              </div>
            </div>

            <p className="text-gray-600 mb-2 text-center">
              Your comprehensive report will be sent to{" "}
              <strong>{email || "your email"}</strong>
            </p>

            <p className="text-sm text-gray-500 text-center">
              Expected delivery: within the next 5-10 minutes
            </p>
          </div>

          <div className="mt-4 text-center">
            <p className="text-sm text-gray-600">
              To ensure you receive your report, please add{" "}
              <strong>hello@lifehack.org</strong> to your address book.{" "}
              <button className="text-blue-600 underline hover:text-blue-700">
                View instructions
              </button>
            </p>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

function ResultsSection({ analysisResult }: { analysisResult: any }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4, duration: 0.6 }}
      className="py-12 bg-white"
    >
      <div className="max-w-4xl mx-auto px-4">
        <h2
          className="text-2xl font-bold text-center mb-8"
          style={{ color: "#2A6354" }}
        >
          Your Assessment Results
        </h2>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Score Display */}
          {analysisResult.score !== undefined && (
            <div className="text-center">
              <div
                className="text-4xl font-bold mb-2"
                style={{ color: "#2A6354" }}
              >
                {analysisResult.score}/100
              </div>
              <p className="text-gray-600">Your Assessment Score</p>
            </div>
          )}

          {/* Category */}
          {analysisResult.category && (
            <div className="text-center">
              <div
                className="text-lg font-semibold mb-2"
                style={{ color: "#2A6354" }}
              >
                {analysisResult.category}
              </div>
              <p className="text-gray-600">Your Profile Type</p>
            </div>
          )}
        </div>

        {/* Insights */}
        {analysisResult.insights && analysisResult.insights.length > 0 && (
          <div className="mt-8">
            <h3 className="text-xl font-semibold mb-4">
              Key Insights About You
            </h3>
            <div className="grid gap-4">
              {analysisResult.insights.map((insight: string, index: number) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 + index * 0.1 }}
                  className="flex items-start p-4 bg-gray-50 rounded-lg"
                >
                  <Target
                    className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0"
                    style={{ color: "#2A6354" }}
                  />
                  <span className="text-gray-700">{insight}</span>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}

function EmotionalMirror({ analysisResult }: { analysisResult: Record<string, unknown> }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3, duration: 0.8 }}
    >
      <EmotionalMirrorContent analysisResult={analysisResult} className="mb-8" />
    </motion.div>
  );
}

function BenefitsSection({ analysisResult }: { analysisResult: any }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6, duration: 0.6 }}
      className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl p-8 md:p-12 border border-emerald-100 mx-4"
    >
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h2
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ color: "#2A6354" }}
          >
            Next Step: Subscribe to LifeHack AI Coach App to get your
            personalized plan
          </h2>
          <p className="text-lg text-gray-700">
            {analysisResult.urgencyMessage}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <div>
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <Target className="w-5 h-5 mr-2" style={{ color: "#2A6354" }} />
              Tailored to Your Results
            </h3>
            <ul className="space-y-3">
              {analysisResult.personalizedBenefits?.map(
                (benefit: string, index: number) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.8 + index * 0.1 }}
                    className="flex items-start"
                  >
                    <Check
                      className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5"
                      style={{ color: "#2A6354" }}
                    />
                    <span className="text-gray-700">{benefit}</span>
                  </motion.li>
                ),
              )}
            </ul>
          </div>

          <div>
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <TrendingUp
                className="w-5 h-5 mr-2"
                style={{ color: "#2A6354" }}
              />
              Expected Results
            </h3>
            <div className="space-y-3">
              <div className="flex items-center">
                <Clock className="w-5 h-5 text-gray-400 mr-2" />
                <span className="text-gray-700">
                  See improvement in 2-4 weeks
                </span>
              </div>
              <div className="flex items-center">
                <Users className="w-5 h-5 text-gray-400 mr-2" />
                <span className="text-gray-700">
                  Join 5,000+ users with similar profiles
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            Over 3,000 people have created personalized plans this month
          </p>
        </div>
      </div>
    </motion.div>
  );
}

export function RealTimeAssessmentResults({
  runId,
  publicAccessToken,
  email,
  assessmentTitle,
  assessmentType,
  isTestMode = false,
  testResultsType = "ai",
}: AssessmentResultsProps) {
  // Use mock data in test mode, real data otherwise
  const realAnalysis = useAssessmentAnalysis(runId, publicAccessToken, assessmentType);
  const mockAnalysis = useMockAssessmentAnalysis(testResultsType, assessmentType);

  const { isComplete, analysisResult } = isTestMode
    ? mockAnalysis
    : realAnalysis;
  const [isResultsExpanded, setIsResultsExpanded] = useState(false);
  const [hasTrackedCompletion, setHasTrackedCompletion] = useState(false);

  // Track CompleteRegistration when results are first displayed
  useEffect(() => {
    if (isComplete && analysisResult && !hasTrackedCompletion && assessmentType) {
      const eventId = generateEventId();
      metaPixelEvents.trackCompleteRegistration(assessmentType, eventId);
      posthog.capture('minitools_results_displayed', {
        assessment_type: assessmentType,
        minitools_assessment_type: assessmentType,
      });
      setHasTrackedCompletion(true);
    }
  }, [isComplete, analysisResult, hasTrackedCompletion, assessmentType]);

  // Build the special offer URL
  const specialOfferUrl = (() => {
    const type = assessmentType || "unknown";
    return buildMinitoolsSpecialOfferPath(type, {
      plan: "quarterly",
      tier: "ai-coach",
      assessment_type: type,
      run_id: runId,
    }, "");
  })();

  const handleClaimDiscount = () => {
    posthog.capture("minitools_assessment_cta_clicked", {
      minitools_assessment_type: assessmentType,
      minitools_assessment_url:
        typeof window !== "undefined" ? window.location.href : "",
      cta_location: "sticky_bottom",
    });
  };

  // Show loading state while analyzing or if no results yet
  if (!isComplete || !analysisResult) {
    return <LoadingState assessmentType={assessmentType} />;
  }

  // Show full results when complete
  return (
    <div className="min-h-screen bg-gray-50">
      <CompletionHero
        category={analysisResult.category}
        assessmentTitle={assessmentTitle}
        email={email}
      />

      {/* Emotional Mirror - personalized narrative */}
      <div className="max-w-4xl mx-auto px-4 pt-6">
        <EmotionalMirror analysisResult={analysisResult} />
      </div>

      {/* Collapsible Results Section */}
      <div className="bg-gray-50 border-gray-200">
        <div className="max-w-4xl mx-auto px-4">
          <button
            onClick={() => setIsResultsExpanded(!isResultsExpanded)}
            className="w-full py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
          >
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                View Detailed Results
              </h3>
              <p className="text-sm text-gray-600">
                See your complete assessment analysis and insights
              </p>
            </div>
            {isResultsExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            )}
          </button>
        </div>

        <AnimatePresence>
          {isResultsExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="overflow-hidden"
            >
              <div className="border-gray-200">
                <ResultsSection analysisResult={analysisResult} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="py-8 pb-16">
        <BenefitsSection analysisResult={analysisResult} />
      </div>

      {/* Sticky Bottom CTA - Value Prop */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg">
        <div className="container mx-auto px-4 py-3 sm:py-4">
          <div className="flex flex-col items-center gap-1.5">
            <Link href={specialOfferUrl}>
              <ContinueButton
                onClick={handleClaimDiscount}
                text="See My Full 90-Day Roadmap"
                fullWidth={false}
              />
            </Link>
            <p className="text-xs text-gray-500">
              Join 12,847+ people who found their path
            </p>
          </div>
        </div>
        {/* Safe area for mobile devices with notch */}
        <div className="pb-safe bg-white" />
      </div>
    </div>
  );
}
